import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePlayer } from "../player/PlayerProvider";
import IconButton from "../components/IconButton";
import { fmt } from "../player/MiniPlayer";
import { RotateBackGlyph, RotateForwardGlyph } from "../player/transportGlyphs";
import { PlayIcon, PauseIcon, VideoIcon, AudioIcon, DownloadIcon } from "../components/icons";
import { LikeButton, BookmarkButton, HeartGlyph, BookmarkGlyph, useEngagementToggles } from "../components/EngagementButtons";
import { getPositionSeconds } from "../api/positionSeconds";
import { trackEvent } from "../../analytics";
import { Scrubber, VolumeControl, MoreMenu, BookGlyph, useElementSize } from "../player/controls";

/*
 * The class page's player card — "docked bar" layout (player redesign,
 * 2026-10): the 16:9 video sits flush in the card (no padding, no inner
 * frame) or a compact audio artwork row in audio mode; under it, one slim
 * control bar — a full-width scrubber strip, then a single row of same-weight
 * borderless controls (play · ↺10 · ↻10 · time · like · bookmark · speed ·
 * volume · ⋯). The ⋯ menu holds the rarely-used items (watch/listen switch,
 * download), plus like/bookmark when the bar is too narrow for them. The
 * centered play button shows only before playback starts. Everything stays
 * visible at rest (no auto-hide, no hover-only controls). Drives the global
 * player, so leaving the page keeps playing.
 */

/**
 * Below this bar width (phones, the half-width split) like and bookmark move
 * into the ⋯ menu and the time moves up beside the scrubber, so play · ↺10 ·
 * ↻10 · speed · mute · (source) · ⋯ still fit one row at 375px. The labelled
 * Source toggle needs more room before the full row fits.
 */
const COMPACT_BAR_WIDTH = 540;
const COMPACT_BAR_WIDTH_WITH_SOURCE = 660;


/*
 * `sourceToggle` ({ open, onToggle }) is passed by ClassMedia when the class
 * has a source text: it adds the 📖 Source toggle to the bar.
 */
export default function PlayerCard({ classData, seriesInfo, sourceToggle = null }) {
  const player = usePlayer();
  const current = useSelector((s) => s.player.current);
  const playing = useSelector((s) => s.player.playing);
  const livePosition = useSelector((s) => s.player.position);
  const liveDuration = useSelector((s) => s.player.duration);
  const speed = useSelector((s) => s.player.speed);
  const muted = useSelector((s) => s.player.muted);
  const volume = useSelector((s) => s.player.volume);

  const { audio, video } = classData;
  const videoAvailable = Boolean(video);
  const audioAvailable = Boolean(audio);
  const [mode, setMode] = useState(videoAvailable ? "video" : "audio");
  const videoMountRef = useRef(null);

  const mediaFor = (m) => (m === "video" ? video : audio);
  const srcFor = (m) => {
    const media = mediaFor(m);
    return media ? media.src || media.streamUrl : null;
  };

  const isCurrentClass = Boolean(current) && [srcFor("video"), srcFor("audio")].filter(Boolean).includes(current.src || current.streamUrl);
  const isCurrentMode = isCurrentClass && current.mediaKind === mode;

  const savedPosition = getPositionSeconds(srcFor(mode));
  const position = isCurrentMode ? livePosition : savedPosition;
  const duration = isCurrentMode && liveDuration > 0 ? liveDuration : audio?.durationSeconds || 0;

  const buildItem = (m) => {
    const media = mediaFor(m);
    return {
      classId: classData.id,
      title: classData.title,
      seriesTitle: seriesInfo?.title || null,
      positionLabel: seriesInfo ? `Class ${seriesInfo.position} of ${seriesInfo.total}` : null,
      mediaKind: m,
      src: media.src,
      streamUrl: media.streamUrl,
      poster: m === "video" ? media.poster : null,
      href: `/${classData.slug}`,
      durationHint: audio?.durationSeconds || 0,
    };
  };

  const loadThisClass = (m, opts = {}) => player.load(buildItem(m), opts);

  const togglePlay = () => {
    if (isCurrentMode) player.toggle();
    else loadThisClass(mode, { autoplay: true, startAt: savedPosition || undefined });
  };

  const switchMode = (m) => {
    if (m === mode) return;
    if (m === "video" && !videoAvailable) return;
    const wasCurrent = isCurrentMode;
    const at = wasCurrent ? player.currentTime() : getPositionSeconds(srcFor(m));
    const wasPlaying = wasCurrent ? playing : false;
    setMode(m);
    if (wasCurrent) loadThisClass(m, { autoplay: wasPlaying, startAt: at });
  };

  // Claim the persistent video element for the card whenever this class's
  // video is (or becomes) the current track and video mode is showing.
  // Priority 2: outranks the mini-player thumb, yields to the expanded sheet.
  useEffect(() => {
    if (mode === "video" && isCurrentMode && videoMountRef.current) {
      return player.claimVideo(videoMountRef.current, 2);
    }
    return undefined;
  }, [mode, isCurrentMode, player]);

  // Tell the mini-player when this class's card is on screen so it can step
  // aside (it would only duplicate the card). ≥30% visible counts.
  const cardRef = useRef(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!isCurrentClass || !el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => player.setCardOnScreen(entry.intersectionRatio >= 0.3), {
      threshold: [0, 0.3, 1],
    });
    io.observe(el);
    return () => {
      io.disconnect();
      player.setCardOnScreen(false);
    };
  }, [isCurrentClass, player]);

  const seek = (sec) => {
    if (isCurrentMode) player.seekTo(sec);
    else loadThisClass(mode, { autoplay: true, startAt: sec });
  };

  const engagement = useEngagementToggles("class", classData.id);
  const barRef = useRef(null);
  const { width: barWidth } = useElementSize(barRef);
  const compact = barWidth > 0 && barWidth < (sourceToggle ? COMPACT_BAR_WIDTH_WITH_SOURCE : COMPACT_BAR_WIDTH);

  const downloadHref = mediaFor(mode)?.src || mediaFor(mode)?.streamUrl;
  const showingPause = isCurrentMode && playing;

  const menuItems = [
    mode === "video"
      ? audioAvailable && { key: "mode", icon: <AudioIcon size={18} />, label: "Listen to audio instead", onSelect: () => switchMode("audio") }
      : videoAvailable
      ? { key: "mode", icon: <VideoIcon size={18} />, label: "Watch video instead", onSelect: () => switchMode("video") }
      : { key: "mode", icon: <VideoIcon size={18} />, label: "Watch video", note: "Audio only", disabled: true },
    downloadHref && {
      key: "download",
      icon: <DownloadIcon size={18} />,
      label: mode === "video" ? "Download video" : "Download audio",
      href: downloadHref,
      onSelect: () => {
        try {
          trackEvent({ category: "player", action: "download", label: classData.title });
        } catch (_) {}
      },
    },
    compact && {
      key: "like",
      icon: <span className={engagement.liked ? "text-ic-sky" : ""}><HeartGlyph filled={engagement.liked} /></span>,
      label: engagement.liked ? "Liked" : "Like this class",
      checked: engagement.liked,
      onSelect: engagement.toggleLike,
    },
    compact && {
      key: "bookmark",
      icon: <span className={engagement.bookmarked ? "text-ic-sky" : ""}><BookmarkGlyph filled={engagement.bookmarked} /></span>,
      label: engagement.bookmarked ? "Bookmarked" : "Bookmark this class",
      checked: engagement.bookmarked,
      onSelect: engagement.toggleBookmark,
    },
  ].filter(Boolean);

  const timeLabel = (
    <span className="text-[13px] leading-4 text-ic-lightGray tabular-nums whitespace-nowrap">
      {fmt(position)}
      <span className="text-white/40"> / </span>
      {duration ? fmt(duration) : "—"}
    </span>
  );

  return (
    <div ref={cardRef} className="bg-ic-navy rounded-xl shadow-ic-raised-dark flex flex-col">
      {/* Media surface — flush with the card edge */}
      {mode === "video" ? (
        <div className="relative rounded-t-xl bg-[#0D1420] overflow-hidden flex items-center justify-center" style={{ aspectRatio: "16/9", maxHeight: "70vh" }}>
          {/* Full-column width would make 16:9 taller than a laptop screen — cap it and letterbox, like YouTube's theater mode */}
          {/* The persistent video element mounts here while current */}
          <div ref={videoMountRef} className="absolute inset-0" />
          {!isCurrentMode && (
            <>
              {/* No poster: just the dark frame and the start prompt — the title is already the page H1 */}
              {video?.poster && <img src={video.poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
              {/* Start prompt — only before playback; afterwards the bar's play button is the one control */}
              <button
                type="button"
                aria-label="Play video"
                onClick={togglePlay}
                className="absolute w-16 h-16 rounded-full bg-[rgba(24,108,206,0.95)] text-ic-offwhite border-0 inline-flex items-center justify-center cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              >
                <PlayIcon size={26} />
              </button>
            </>
          )}
          {isCurrentMode && (
            <button
              type="button"
              aria-label={playing ? "Pause video" : "Play video"}
              tabIndex={-1}
              onClick={togglePlay}
              className="absolute inset-0 w-full h-full bg-transparent border-0 cursor-pointer focus-visible:outline-none"
            />
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 pt-4 pb-1">
          <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-ic-navyCard flex flex-col items-center justify-center">
            <span dir="rtl" className="text-[16px] font-extrabold text-ic-sky leading-5">שיעור</span>
            <span className="text-[7px] font-bold tracking-[0.5px] text-ic-lightGray uppercase">Audio</span>
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="text-[16px] font-semibold leading-6 text-white ic-wrap-anywhere">{classData.title}</span>
            {seriesInfo && (
              <span className="text-[14px] leading-5 text-ic-lightGray">
                Class {seriesInfo.position} of {seriesInfo.total} · {seriesInfo.title}
              </span>
            )}
            {!videoAvailable && <span className="text-[12px] leading-4 text-ic-lightGray">Audio only</span>}
          </div>
        </div>
      )}

      {/* Control bar — scrubber strip, then one row of same-weight controls */}
      <div ref={barRef} className="px-2 pb-1.5">
        <div className="px-2">
          {compact ? (
            <div className="flex items-center gap-2.5">
              <Scrubber value={position} max={duration || 1} onSeek={seek} trackHeight={4} thumb={12} />
              {timeLabel}
            </div>
          ) : (
            <Scrubber value={position} max={duration || 1} onSeek={seek} trackHeight={4} thumb={12} />
          )}
        </div>
        <div className="flex items-center">
          <IconButton variant="bareDark" size={44} dark className="text-white" label={showingPause ? "Pause" : "Play"} onClick={togglePlay}>
            {showingPause ? <PauseIcon size={22} /> : <PlayIcon size={22} />}
          </IconButton>
          <IconButton variant="bareDark" size={44} dark label="Back 10 seconds" onClick={() => isCurrentMode && player.seekBy(-10)}>
            <RotateBackGlyph size={22} />
          </IconButton>
          <IconButton variant="bareDark" size={44} dark label="Forward 10 seconds" onClick={() => isCurrentMode && player.seekBy(10)}>
            <RotateForwardGlyph size={22} />
          </IconButton>
          {!compact && <span className="pl-1.5">{timeLabel}</span>}
          <div className="flex-1" />
          {!compact && (
            <>
              <LikeButton target="class" id={classData.id} surface="navyBare" />
              <BookmarkButton target="class" id={classData.id} surface="navyBare" />
            </>
          )}
          <button
            type="button"
            aria-label={`Playback speed ${speed}×`}
            onClick={player.cycleSpeed}
            className="min-w-[44px] h-11 px-1.5 rounded-full bg-transparent border-0 text-ic-lightGray font-sans text-[14px] font-bold tabular-nums cursor-pointer transition-all duration-150 ease-out hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
          >
            {speed}×
          </button>
          {/* Mute stays in the bar at every width; the slide-out slider only when there's room */}
          <VolumeControl muted={muted} volume={volume} slider={!compact} onMute={() => player.setMuted(!muted)} onVolume={(v) => player.setVolume(v)} />
          {sourceToggle && (
            <button
              type="button"
              aria-pressed={sourceToggle.open}
              aria-label={sourceToggle.open ? "Hide source text" : "Show source text"}
              onClick={sourceToggle.onToggle}
              className={`h-11 min-w-[44px] px-2.5 rounded-full inline-flex items-center justify-center gap-1.5 bg-transparent border-0 font-sans text-[14px] font-bold cursor-pointer transition-all duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:shadow-ic-focus-dark ${
                sourceToggle.open ? "text-ic-sky" : "text-ic-lightGray hover:text-white"
              }`}
            >
              <BookGlyph />
              {!compact && <span>Source</span>}
            </button>
          )}
          <MoreMenu items={menuItems} />
        </div>
      </div>
    </div>
  );
}
